import { Stack, type HairlineProps } from "@lucasmarkes/hairline/react";
import { Link, useNavigate, useParams } from "@tanstack/react-router";
import {
  AlertCircleIcon,
  CheckCircle2Icon,
  FileTextIcon,
  LoaderCircleIcon,
  MoreHorizontalIcon,
  SearchIcon,
  UploadIcon,
} from "lucide-react";
import { useMemo, useState, type ComponentType } from "react";

import { HairlineArt } from "~/components/ui/hairline-art";
import { cn } from "~/lib/utils";

import { UNAVAILABLE_MESSAGES, useOrgProfile } from "../../state/orgProfile";
import {
  isKnowledgeDocument,
  policyDisplayName,
  usePolicies,
  useRegulatoryUpdates,
  type Policy,
} from "../../state/policies";
import {
  useOrganizationFindings,
  useRecommendations,
  useOrgGovernance,
  useOwnersByPolicy,
  type OrganizationPerson,
  type OrgPolicyGovernance,
} from "../../state/governance";
import { PolicyRecommendations } from "./policy/PolicyRecommendations";
import { PersonAvatars } from "./PersonAvatars";
import { PolicyGovernanceCards } from "./PolicyGovernanceCards";
import { PolicyUploadDialog } from "./PolicyUploadDialog";
import { FRESHNESS_LABELS, FRESHNESS_PILL, policyFreshness } from "./policy/policyFreshness";
import { OpenWorkBadges } from "./policy/OpenWorkBadges";
import { NO_OPEN_WORK, openFindingsByPolicy, openWorkByPolicy, type OpenWork } from "./policy/openWorkByPolicy";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Menu, MenuItem, MenuPopup, MenuTrigger } from "../ui/menu";
import { InputGroup, InputGroupAddon, InputGroupInput } from "../ui/input-group";
import { Select, SelectContent, SelectItem, SelectTrigger } from "../ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import { useRegulationOrg } from "./useRegulationOrg";

/**
 * "2 days ago" without a date library for one call site. Coarse on purpose: a
 * policy's exact upload minute is never the question being asked.
 */
function relativeTime(value: string): string {
  const then = new Date(value).getTime();
  if (Number.isNaN(then)) return "";
  const seconds = Math.max(0, Math.round((Date.now() - then) / 1000));
  const steps: ReadonlyArray<readonly [number, number, Intl.RelativeTimeFormatUnit]> = [
    [60, 1, "second"],
    [3600, 60, "minute"],
    [86400, 3600, "hour"],
    [2592000, 86400, "day"],
    [31536000, 2592000, "month"],
    [Number.POSITIVE_INFINITY, 31536000, "year"],
  ];
  const formatter = new Intl.RelativeTimeFormat(undefined, { numeric: "auto" });
  for (const [limit, divisor, unit] of steps) {
    if (seconds < limit) return formatter.format(-Math.round(seconds / divisor), unit);
  }
  return "";
}

/** Processing older than this is a dead job, not work in flight. */
const PROCESSING_STALL_MS = 24 * 60 * 60 * 1000;

function StatusBadge({ status, uploadedAt }: { status: string; uploadedAt?: string }) {
  if (status === "ready") {
    // The healthy state earns the workspace's success tint (the idiom
    // ThreadStatusIndicators uses); neutral gray reads as "disabled".
    return (
      <span className="flex w-fit shrink-0 items-center gap-1 rounded-full bg-emerald-600/10 px-2 py-0.5 text-xs font-medium text-emerald-600 dark:text-emerald-300/90">
        <CheckCircle2Icon className="size-3" />
        Ready
      </span>
    );
  }
  if (status === "processing") {
    // A spinner promises progress. After a day without finishing, that
    // promise is a lie — say the job stalled and needs a re-upload instead.
    const started = uploadedAt === undefined ? Number.NaN : Date.parse(uploadedAt);
    const stalled = !Number.isNaN(started) && Date.now() - started > PROCESSING_STALL_MS;
    if (stalled) {
      return (
        <span
          title="Processing never finished. Re-upload the document to retry."
          className="flex w-fit shrink-0 items-center gap-1 rounded-full bg-destructive/10 px-2 py-0.5 text-xs font-medium text-destructive"
        >
          <AlertCircleIcon className="size-3" />
          Stalled
        </span>
      );
    }
    return (
      <span className="flex w-fit shrink-0 items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">
        <LoaderCircleIcon className="size-3 animate-spin" />
        Processing
      </span>
    );
  }
  return (
    <span className="flex w-fit shrink-0 items-center gap-1 rounded-full bg-destructive/10 px-2 py-0.5 text-xs text-destructive">
      <AlertCircleIcon className="size-3" />
      Failed
    </span>
  );
}

function EmptyState({
  title,
  description,
  art,
}: {
  title: string;
  description: string;
  art?: ComponentType<HairlineProps>;
}) {
  return (
    <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-border/60 bg-card px-6 py-16 text-center">
      {art ? (
        <HairlineArt figure={art} surface="card" className="mb-4 w-48 sm:w-56" />
      ) : (
        <span className="mb-3 flex size-12 items-center justify-center rounded-full bg-muted text-muted-foreground">
          <FileTextIcon className="size-5" />
        </span>
      )}
      <p className="text-base font-medium text-foreground">{title}</p>
      <p className="mt-1 max-w-sm text-sm text-muted-foreground">{description}</p>
    </div>
  );
}

const ALL = "__all__";

const STATUS_LABELS: Readonly<Record<string, string>> = {
  [ALL]: "All statuses",
  ready: "Ready",
  processing: "Processing",
  failed: "Failed",
};

/**
 * One document. Every cell is fixed-shape — jurisdictions overflow into a
 * "+n" badge rather than wrapping — so row height never varies with the
 * metadata a document happens to carry.
 */
const FREQUENCY_SHORT: Record<string, string> = {
  none: "—",
  monthly: "Monthly",
  quarterly: "Quarterly",
  semiannual: "6-monthly",
  yearly: "Yearly",
};

function PolicyTableRow({
  policy,
  governance,
  touched,
  now,
  orgSegment,
  jurisdictionNames,
  showJurisdictions,
  showGovernance,
  owners,
  openWork,
  onOpen,
  onDownload,
  onRemove,
  canManage,
}: {
  policy: Policy;
  governance: OrgPolicyGovernance | undefined;
  /** Named by a regulatory update — out of date until amended. */
  touched: boolean;
  now: number;
  orgSegment: string;
  jurisdictionNames: ReadonlyMap<string, string>;
  showJurisdictions: boolean;
  showGovernance: boolean;
  owners: ReadonlyArray<OrganizationPerson>;
  /** Findings and suggestions still waiting on a decision, counted apart. */
  openWork: OpenWork;
  onOpen: () => void;
  onDownload: (policyId: string) => void;
  onRemove: (policyId: string) => void;
  canManage: boolean;
}) {
  const labels = policy.jurisdictionSlugs.map((slug) => jurisdictionNames.get(slug) ?? slug);

  return (
    <TableRow className="h-14 cursor-pointer" onClick={onOpen}>
      <TableCell className="px-4">
        <div className="flex items-center gap-3">
          <span className="flex size-8 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground">
            <FileTextIcon className="size-4" />
          </span>
          {/* The link keeps the row keyboard-reachable and right-clickable;
              the row click is the pointer affordance. */}
          <Link
            to="/$org/compliance/policies/$policyId"
            params={{ org: orgSegment, policyId: policy.id }}
            onClick={(event) => event.stopPropagation()}
            className={cn(
              "block max-w-[160px] truncate text-sm font-medium text-foreground hover:underline sm:max-w-[220px]",
              // With governance and jurisdiction columns on screen the name
              // has to share the row; alone, it can take the room they leave.
              showJurisdictions && showGovernance
                ? "lg:max-w-[220px] xl:max-w-[280px]"
                : "lg:max-w-[420px] xl:max-w-[560px]",
            )}
          >
            {policyDisplayName(policy)}
          </Link>
        </div>
      </TableCell>

      {showJurisdictions && (
      <TableCell className="hidden px-4 md:table-cell">
        {labels.length === 0 ? (
          <span className="text-xs text-muted-foreground">—</span>
        ) : (
          <div className="flex items-center gap-1">
            {labels.slice(0, 2).map((label) => (
              <Badge
                key={label}
                variant="outline"
                className="rounded-full border-border/60 px-2 py-0 text-[10px] font-normal"
              >
                {label}
              </Badge>
            ))}
            {labels.length > 2 ? (
              <Badge
                variant="outline"
                className="rounded-full border-border/60 px-2 py-0 text-[10px] font-normal"
              >
                +{labels.length - 2}
              </Badge>
            ) : null}
          </div>
        )}
      </TableCell>
      )}

      {showGovernance && (
      <TableCell className="hidden px-4 md:table-cell">
        <div onClick={(event) => event.stopPropagation()}>
          <PersonAvatars people={owners} emptyLabel="Unassigned" />
        </div>
      </TableCell>
      )}

      {showGovernance && (
      <TableCell className="hidden px-4 lg:table-cell">
        {governance === undefined || governance.reviewFrequency === "none" ? (
          <span className="text-xs text-muted-foreground">No cycle</span>
        ) : (
          <span className="text-xs text-foreground">
            {FREQUENCY_SHORT[governance.reviewFrequency]}
            {governance.reviewDueAt !== null && (
              <span
                className={
                  Date.parse(governance.reviewDueAt) < Date.now()
                    ? "block text-destructive"
                    : "block text-muted-foreground"
                }
              >
                {Date.parse(governance.reviewDueAt) < Date.now() ? "overdue " : "due "}
                {new Date(governance.reviewDueAt).toLocaleDateString(undefined, {
                  month: "short",
                  day: "numeric",
                })}
              </span>
            )}
          </span>
        )}
      </TableCell>
      )}

      <TableCell className="hidden px-4 lg:table-cell">
        {/* What the reviewer is here for: what still needs deciding. The
            upload date is on the document itself. */}
        <OpenWorkBadges work={openWork} />
      </TableCell>

      <TableCell className="px-4">
        {(() => {
          // Processing states keep the live badge (it carries the "stuck?"
          // hint); everything else shows freshness, the same word the policy
          // page uses.
          if (policy.status !== "ready") {
            return <StatusBadge status={policy.status} uploadedAt={policy.createdAt} />;
          }
          const freshness = policyFreshness({
            policy,
            governance: governance ?? null,
            touchedByChange: touched,
            openFindings: openWork.findings,
            now,
          });
          return (
            <span
              className={`flex w-fit shrink-0 items-center rounded-full px-2 py-0.5 text-xs ${FRESHNESS_PILL[freshness]}`}
            >
              {FRESHNESS_LABELS[freshness]}
            </span>
          );
        })()}
      </TableCell>

      <TableCell className="px-4">
        {/* Row actions stop propagation: opening the menu must not also
            navigate to the document. */}
        <div onClick={(event) => event.stopPropagation()}>
          <Menu>
            <MenuTrigger
              render={
                <Button
                  type="button"
                  size="icon-xs"
                  variant="ghost"
                  aria-label={`Actions for ${policyDisplayName(policy)}`}
                />
              }
            >
              <MoreHorizontalIcon className="size-4" />
            </MenuTrigger>
            <MenuPopup align="end">
              <MenuItem onClick={() => onDownload(policy.id)}>Download</MenuItem>
              {canManage ? <MenuItem onClick={() => onRemove(policy.id)}>Remove</MenuItem> : null}
            </MenuPopup>
          </Menu>
        </div>
      </TableCell>
    </TableRow>
  );
}

/**
 * The organization's policy library as a table: fixed columns, so every row
 * is the same height and shape whatever metadata a document carries, with
 * real filtering over the set rather than chips that only decorate.
 */
export function PoliciesSection({
  documentKind = "policy",
  initialQuery = "",
}: {
  /**
   * Which shelf this page is. Policies and procedures are the same records
   * with the same governance, findings and versions — the only difference is
   * which of them a page shows, so both render this component.
   */
  readonly documentKind?: "policy" | "procedure";
  readonly initialQuery?: string;
} = {}) {
  const { org, isLoading } = useRegulationOrg();
  const { snapshot, unavailableReason, isPending, download, remove } =
    usePolicies(
    org?.id ?? null,
  );
  const { canManage } = useOrgProfile(org?.id ?? null);
  const {
    byPolicyId: governanceByPolicy,
    snapshot: governanceSnapshot,
    isPending: governancePending,
    unavailableReason: governanceUnavailable,
  } = useOrgGovernance(org?.id ?? null);
  const { byPolicyId: ownersByPolicy } = useOwnersByPolicy(org?.id ?? null);
  const { snapshot: findingSnapshot } = useOrganizationFindings(org?.id ?? null);
  // Org-wide: one request for the whole shelf rather than one per row.
  const { snapshot: recommendationSnapshot } = useRecommendations(org?.id ?? null);
  // Findings (red) and suggestions (neutral) apart: adding housekeeping
  // suggestions into the red count made a suggested rename read as a finding.
  // A suggestion with no policy (a missing document) belongs to the library,
  // not a row, and is shown above it.
  const openWork = useMemo(
    () => openWorkByPolicy(findingSnapshot?.findings ?? [], recommendationSnapshot?.recommendations ?? []),
    [findingSnapshot, recommendationSnapshot],
  );
  const openFindings = useMemo(() => openFindingsByPolicy(openWork), [openWork]);
  const { snapshot: updates } = useRegulatoryUpdates(org?.id ?? null);
  // Policies a regulatory update names are out of date until amended; one
  // clock reading per render keeps every row and the chart on the same "now".
  const touchedPolicyIds = useMemo(
    () => new Set((updates?.updates ?? []).flatMap((update) => update.affectedPolicyIds)),
    [updates],
  );
  const now = Date.now();
  const navigate = useNavigate();
  // The tenant segment comes from the URL these rows live under, so a row can
  // never link into a different organization than the page.
  const orgSegment = useParams({
    strict: false,
    select: (params) => (params as { org?: string }).org,
  });

  const [query, setQuery] = useState(initialQuery);
  // Drag depth, not a boolean: dragging over a child fires dragleave on the
  // parent, and a boolean would flicker the overlay off mid-drag.
  const [dragDepth, setDragDepth] = useState(0);
  const [dropError, setDropError] = useState<string | null>(null);
  const [droppedFiles, setDroppedFiles] = useState<ReadonlyArray<File>>([]);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [status, setStatus] = useState<string>(ALL);
  const [jurisdiction, setJurisdiction] = useState<string>(ALL);

  const policies = useMemo(
    () =>
      (snapshot?.policies ?? []).filter(
        // Knowledge-base files live on the Knowledge page, not this shelf.
        (policy) => (policy.documentKind ?? "policy") === documentKind && !isKnowledgeDocument(policy),
      ),
    [snapshot, documentKind],
  );
  const noun = documentKind === "procedure" ? "procedure" : "policy";
  // "policys" is how a product tells you nobody read its own screen.
  const plural = documentKind === "procedure" ? "procedures" : "policies";
  const jurisdictions = snapshot?.jurisdictions ?? [];
  const jurisdictionNames = new Map(jurisdictions.map((item) => [item.slug, item.name]));

  // A column where every cell is the same placeholder ("—", "Unassigned")
  // carries no information — drop it until at least one row has something to
  // say. The governance setup card above the table explains where owners and
  // review cycles come from.
  const showJurisdictions = policies.some((policy) => policy.jurisdictionSlugs.length > 0);
  const showGovernance = policies.some(
    (policy) => governanceByPolicy.has(policy.id) || (ownersByPolicy.get(policy.id)?.length ?? 0) > 0,
  );
  const columnCount = 4 + (showJurisdictions ? 1 : 0) + (showGovernance ? 2 : 0);

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return policies.filter((policy) => {
      if (status !== ALL && policy.status !== status) return false;
      if (jurisdiction !== ALL && !policy.jurisdictionSlugs.includes(jurisdiction)) return false;
      if (
        needle.length > 0 &&
        !policy.filename.toLowerCase().includes(needle) &&
        !policyDisplayName(policy).toLowerCase().includes(needle)
      )
        return false;
      return true;
    });
  }, [jurisdiction, policies, query, status]);

  if (org === null) {
    return (
      <div className="mt-6">
        <EmptyState
          title={isLoading ? "Loading your organizations…" : "No organization selected"}
          description={`Choose an organization to see its ${noun} library.`}
        />
      </div>
    );
  }

  if (unavailableReason !== null) {
    return (
      <div className="mt-6">
        <EmptyState
          title="Policies are unavailable"
          description={UNAVAILABLE_MESSAGES[unavailableReason]}
        />
      </div>
    );
  }

  if (policies.length === 0) {
    return (
      <div className="mt-6">
        {/* The library's own suggestions — a missing document above all —
            exist before any document does; an empty shelf still shows them. */}
        {documentKind === "policy" ? (
          <div className="mb-4">
            <PolicyRecommendations organizationId={org?.id ?? null} policyId={null} heading="Missing from the library" />
          </div>
        ) : null}
        <EmptyState
          {...(isPending ? {} : { art: Stack })}
          title={
            isPending
              ? `Loading ${plural}…`
              : documentKind === "procedure"
                ? "No procedures yet"
                : "No policies yet"
          }
          description={
            documentKind === "procedure"
              ? "Procedures describe how a policy is carried out day to day. Move a document here from its Overview tab, or upload one."
              : `Upload a PDF to add it to ${org.name}'s compliance knowledge base.`
          }
        />
      </div>
    );
  }

  // Dropped files open the upload dialog with the drop already in it, so a
  // drop gets the same new-or-version and jurisdiction choices as the button.
  const acceptDropped = (files: FileList | null) => {
    const pdfs = Array.from(files ?? []).filter((candidate) =>
      candidate.name.toLowerCase().endsWith(".pdf"),
    );
    if (pdfs.length === 0) {
      setDropError("Only PDF policies can be uploaded.");
      return;
    }
    setDropError(null);
    setDroppedFiles(pdfs);
    setUploadOpen(true);
  };

  return (
    <div
      className="relative mt-6"
      onDragEnter={(event) => {
        if (!canManage) return;
        event.preventDefault();
        setDragDepth((depth) => depth + 1);
      }}
      onDragOver={(event) => {
        if (!canManage) return;
        event.preventDefault();
      }}
      onDragLeave={() => {
        if (!canManage) return;
        setDragDepth((depth) => Math.max(0, depth - 1));
      }}
      onDrop={(event) => {
        if (!canManage) return;
        event.preventDefault();
        setDragDepth(0);
        acceptDropped(event.dataTransfer?.files ?? null);
      }}
    >
      {dragDepth > 0 ? (
        <div className="pointer-events-none absolute inset-0 z-20 flex items-center justify-center rounded-lg border-2 border-dashed border-primary/50 bg-background/80 backdrop-blur-[1px]">
          <p className="flex items-center gap-2 text-sm font-medium text-foreground">
            <UploadIcon className="size-4" />
            Drop a PDF to add it to this library
          </p>
        </div>
      ) : null}

      {dropError ? (
        <p role="alert" className="mb-2 text-xs text-destructive">
          {dropError}
        </p>
      ) : null}
      <PolicyUploadDialog
        organizationId={org.id}
        open={uploadOpen}
        onOpenChange={setUploadOpen}
        initialFiles={droppedFiles}
      />

      <PolicyGovernanceCards
        policies={policies}
        governance={governanceByPolicy}
        touchedPolicyIds={touchedPolicyIds}
        openFindings={openFindings}
        // First load only: a refresh keeps whatever the cards already say.
        isLoading={governancePending && governanceSnapshot === null}
        unavailable={governanceUnavailable !== null}
        className="mb-4"
        setupAction={
          policies[0] !== undefined && orgSegment ? (
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="shrink-0 cursor-pointer"
              render={
                <Link
                  to="/$org/compliance/policies/$policyId"
                  params={{ org: orgSegment, policyId: policies[0].id }}
                />
              }
            >
              Open first policy
            </Button>
          ) : undefined
        }
      />

      <div className="mb-3 flex flex-wrap items-center gap-2">
        <div className="relative min-w-56 flex-1">
          <InputGroup className="h-8">
            <InputGroupAddon className="text-muted-foreground">
              <SearchIcon className="size-3.5" />
            </InputGroupAddon>
            <InputGroupInput
              value={query}
              onChange={(event) => setQuery(event.currentTarget.value)}
              placeholder="Search documents"
              aria-label="Search documents"
              className="text-sm"
            />
          </InputGroup>
        </div>

        <Select value={status} onValueChange={(value) => setStatus(value ?? ALL)}>
          <SelectTrigger size="sm" className="w-36" aria-label="Filter by status">
            {/* Render the label ourselves: the sentinel is an implementation
                detail and must never surface as "__all__". */}
            <span className="truncate">{STATUS_LABELS[status] ?? "All statuses"}</span>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>All statuses</SelectItem>
            <SelectItem value="ready">Ready</SelectItem>
            <SelectItem value="processing">Processing</SelectItem>
            <SelectItem value="failed">Failed</SelectItem>
          </SelectContent>
        </Select>

        {jurisdictions.length > 0 && showJurisdictions ? (
          <Select value={jurisdiction} onValueChange={(value) => setJurisdiction(value ?? ALL)}>
            <SelectTrigger size="sm" className="w-44" aria-label="Filter by jurisdiction">
              <span className="truncate">
                {jurisdiction === ALL
                  ? "All jurisdictions"
                  : (jurisdictionNames.get(jurisdiction) ?? jurisdiction)}
              </span>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All jurisdictions</SelectItem>
              {jurisdictions.map((item) => (
                <SelectItem key={item.slug} value={item.slug}>
                  {item.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : null}

        <span className="ms-auto shrink-0 pe-1 text-xs text-muted-foreground tabular-nums">
          {filtered.length === policies.length
            ? `${policies.length} ${policies.length === 1 ? noun : plural}`
            : `${filtered.length} of ${policies.length} ${plural}`}
        </span>
      </div>

      {/* What the inspector says the library lacks has no row to live on;
          it sits above the shelf, on the Policies side only, so it is said
          once. */}
      {documentKind === "policy" ? (
        <PolicyRecommendations
          organizationId={org?.id ?? null}
          policyId={null}
          heading="Missing from the library"
        />
      ) : null}

      {/* overflow-x-auto, never hidden: a clipped Status column is a lie. */}
      <div className="overflow-x-auto rounded-xl border border-border/60 bg-card">
        <Table>
          <TableHeader>
            <TableRow className="border-b bg-muted/50 hover:bg-muted/50">
              <TableHead className="h-10 px-4 font-medium text-foreground/80">Document</TableHead>
              {showJurisdictions && (
                <TableHead className="hidden h-10 w-[190px] px-4 font-medium text-foreground/80 md:table-cell">
                  Jurisdictions
                </TableHead>
              )}
              {showGovernance && (
                <TableHead className="hidden h-10 w-[150px] px-4 font-medium text-foreground/80 md:table-cell">
                  Owner
                </TableHead>
              )}
              {showGovernance && (
                <TableHead className="hidden h-10 w-[110px] px-4 font-medium text-foreground/80 lg:table-cell">
                  Review
                </TableHead>
              )}
              {/* Findings (red) and suggestions (neutral), side by side. */}
              <TableHead className="hidden h-10 w-[130px] px-4 font-medium text-foreground/80 lg:table-cell">
                Open
              </TableHead>
              <TableHead className="h-10 w-[140px] px-4 font-medium text-foreground/80">Status</TableHead>
              <TableHead className="h-10 w-[56px] px-4">
                <span className="sr-only">Actions</span>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.length === 0 ? (
              <TableRow className="hover:bg-transparent">
                <TableCell
                  colSpan={columnCount}
                  className="h-24 text-center text-sm text-muted-foreground"
                >
                  No documents match these filters.
                </TableCell>
              </TableRow>
            ) : (
              filtered.map((policy) => (
                <PolicyTableRow
                  key={policy.id}
                  policy={policy}
                  governance={governanceByPolicy.get(policy.id)}
                  touched={touchedPolicyIds.has(policy.id)}
                  now={now}
                  orgSegment={orgSegment ?? ""}
                  jurisdictionNames={jurisdictionNames}
                  showJurisdictions={showJurisdictions}
                  showGovernance={showGovernance}
                  owners={ownersByPolicy.get(policy.id) ?? []}
                  openWork={openWork.get(policy.id) ?? NO_OPEN_WORK}
                  canManage={canManage}
                  onDownload={download}
                  onRemove={(policyId) => void remove(policyId)}
                  onOpen={() => {
                    if (!orgSegment) return;
                    void navigate({
                      to: "/$org/compliance/policies/$policyId",
                      params: { org: orgSegment, policyId: policy.id },
                    });
                  }}
                />
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
